import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { FunnelCategory, FunnelFilters } from '@/types/funnel.ts';
import { hasActiveFunnelFilters } from '../utils/funnelUtils';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';

interface PageOption {
    id: string;
    name: string;
}

interface FunnelFiltersProps {
    pages: PageOption[];
    categories?: FunnelCategory[];
    filters: FunnelFilters;
    onFiltersChange: (filters: FunnelFilters) => void;
}

export default function FunnelFiltersComponent({
    pages,
    categories = [],
    filters,
    onFiltersChange
}: FunnelFiltersProps) {
    // Voltando do builder com filtro guardado, o painel precisa abrir sozinho —
    // senão a listagem aparece cortada sem nada na tela explicando por quê.
    const [isOpen, setIsOpen] = useState(() => hasActiveFunnelFilters(filters));
    const filtrosAtivos = hasActiveFunnelFilters(filters);

    return (
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a]/5">
                        <SlidersHorizontal className="h-4 w-4 text-[#0f172a]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Filtros</h3>
                    {filtrosAtivos && (
                        <span className="px-2 py-0.5 rounded-full bg-[#0f172a] text-white text-[10px] font-semibold uppercase tracking-wider">
                            Ativos
                        </span>
                    )}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <div className="px-6 pb-6 pt-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                    Página
                                </label>
                                <SearchableSelect
                                    value={filters.page || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, page: value === 'all' ? undefined : value })
                                    }
                                    options={[
                                        { value: 'all', label: 'Todas as páginas' },
                                        ...pages.map((page) => ({ value: page.id, label: page.name })),
                                    ]}
                                    placeholder="Todas as páginas"
                                    searchPlaceholder="Buscar por nome ou ID..."
                                />
                            </div>

                            {categories.length > 0 && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                        Categoria
                                    </label>
                                    <SearchableSelect
                                        value={filters.categoryId != null ? String(filters.categoryId) : 'all'}
                                        onValueChange={(value) =>
                                            onFiltersChange({ ...filters, categoryId: value === 'all' ? undefined : Number(value) })
                                        }
                                        options={[
                                            { value: 'all', label: 'Todas as categorias' },
                                            ...categories.map((category) => ({ value: String(category.id), label: category.name })),
                                        ]}
                                        placeholder="Todas as categorias"
                                        searchPlaceholder="Buscar categoria..."
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                    Status
                                </label>
                                <Select
                                    value={filters.status || 'all'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, status: value === 'all' ? undefined : (value as 'active' | 'inactive') })
                                    }
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue placeholder="Todos os status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="active">Ativos</SelectItem>
                                        <SelectItem value="inactive">Inativos</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                    Ordenar por
                                </label>
                                <Select
                                    value={filters.sortBy || 'created_at'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortBy: value as 'created_at' | 'nome' })
                                    }
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="created_at">Data de criação</SelectItem>
                                        <SelectItem value="nome">Nome</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                                    Ordem
                                </label>
                                <Select
                                    value={filters.sortOrder || 'desc'}
                                    onValueChange={(value) =>
                                        onFiltersChange({ ...filters, sortOrder: value as 'asc' | 'desc' })
                                    }
                                >
                                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="desc">Decrescente</SelectItem>
                                        <SelectItem value="asc">Crescente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {filtrosAtivos && (
                            <div className="mt-4 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() =>
                                        onFiltersChange({
                                            sortBy: filters.sortBy,
                                            sortOrder: filters.sortOrder,
                                        })
                                    }
                                    className="text-xs font-medium text-gray-500 hover:text-gray-900 underline underline-offset-4 transition-colors"
                                >
                                    Limpar filtros
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}